import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useUpdateIdentity } from '@/hooks/useIdentityActions';
import { createLogger } from '@/lib/logger';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

const log = createLogger('UpdateUserDialog');

export function UpdateUserDialog({
  isOpen,
  onClose,
  defaultValues,
  identityId,
}: {
  isOpen: boolean;
  onClose: () => void;
  defaultValues: {
    firstName: string;
    lastName: string;
  };
  identityId: string;
}) {
  const [firstName, setFirstName] = useState(defaultValues.firstName);
  const [lastName, setLastName] = useState(defaultValues.lastName);

  const { mutate: updateIdentity, isPending } = useUpdateIdentity();

  const handleSubmit = () => {
    updateIdentity(
      {
        id: identityId,
        updateInput: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
        },
      },
      {
        onSuccess: () => {
          onClose();
          toast.success(
            `L'utilisateur ${firstName} ${lastName} a été mis à jour avec succès`
          );
        },
        onError: (error) => {
          log.error('Error updating user identity', error);
          toast.error(
            `Une erreur est survenue lors de la mise à jour de l'utilisateur ${firstName} ${lastName}`
          );
        },
      }
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Modifier l'utilisateur</DialogTitle>
          <DialogDescription>
            Modifier le nom et prénom de l'utilisateur de cette ligne.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <label htmlFor="firstName" className="text-sm font-medium">
              Prénom
            </label>
            <input
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <div className="grid gap-2">
            <label htmlFor="lastName" className="text-sm font-medium">
              Nom
            </label>
            <input
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
        </div>
        <DialogFooter className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!firstName.trim() || !lastName.trim() || isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Enregistrement...
              </>
            ) : (
              'Enregistrer'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
